import { useEffect, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { Button } from '../components/Button'
import { DuplicatePairCard, GiftRoleTag } from '../components/DuplicatePairCard'
import { giftRole } from '../lib/giftRole'
import { Panel } from '../components/Panel'
import {
  api,
  type ApplyAutoMergesResponse,
  type DuplicateAutoMergedItem,
  type DuplicateCandidate,
  type DuplicateDetailResponse,
  type DuplicateGiftLink,
  type DuplicateListResponse,
  type DuplicateSubscriberDetail,
  type DuplicateSubscriberSummary,
  type DuplicateTier,
  type UpstreamCleanupItem,
} from '../api/client'
import { publicationName } from '../lib/format'

type Tab = 'needs_review' | 'auto_merged' | 'upstream_cleanup' | 'dismissed'

const TAB_LABEL: Record<Tab, string> = {
  needs_review: 'Needs review',
  auto_merged: 'Auto-merged',
  upstream_cleanup: 'Upstream cleanup',
  dismissed: 'Dismissed',
}

export function DuplicatesPage() {
  const [tab, setTab] = useState<Tab>('needs_review')
  const [activeCandidate, setActiveCandidate] = useState<number | null>(null)

  return (
    <div className="p-6 space-y-5">
      <header>
        <div className="label">Data Quality</div>
        <h1 className="font-serif text-3xl text-paper mt-1">Duplicate Subscribers</h1>
        <p className="text-paper-dim text-[13px] mt-2 max-w-3xl">
          Pairs the detection pipeline has flagged as likely the same person across
          multiple SimpleCirc account IDs. Merging folds the loser account's
          subscriptions and order history into the canonical row; the loser row stays
          aliased so future webhooks route correctly. Auto-merges are reversible for 7
          days.
        </p>
      </header>

      <nav className="flex gap-1 border-b border-ink-500">
        {(['needs_review', 'auto_merged', 'upstream_cleanup', 'dismissed'] as Tab[]).map((t) => (
          <button
            key={t}
            onClick={() => { setTab(t); setActiveCandidate(null) }}
            className={`px-4 py-2 text-[13px] font-serif tracking-tight border-b-2 -mb-px ${
              tab === t
                ? 'text-amber-glow border-amber-glow'
                : 'text-paper-dim border-transparent hover:text-paper'
            }`}
          >
            {TAB_LABEL[t]}
          </button>
        ))}
      </nav>

      {tab === 'needs_review' && (
        <NeedsReviewTab
          activeCandidate={activeCandidate}
          setActiveCandidate={setActiveCandidate}
        />
      )}
      {tab === 'auto_merged' && <AutoMergedTab />}
      {tab === 'upstream_cleanup' && <UpstreamCleanupTab />}
      {tab === 'dismissed' && <DismissedTab />}
    </div>
  )
}

// ---------- Needs review ----------

function NeedsReviewTab({
  activeCandidate, setActiveCandidate,
}: {
  activeCandidate: number | null
  setActiveCandidate: (id: number | null) => void
}) {
  const qc = useQueryClient()
  const [search, setSearch] = useState('')
  const [q, setQ] = useState('')
  useEffect(() => {
    const t = setTimeout(() => setQ(search.trim().length >= 2 ? search.trim() : ''), 300)
    return () => clearTimeout(t)
  }, [search])
  // Pull a generous page so tier groups render with enough depth. The
  // backend orders by score desc, so high-confidence stuff lands first;
  // anything past the page is reached with the search box.
  const { data, isLoading } = useQuery({
    queryKey: ['duplicates', 'needs_review', q],
    queryFn: () =>
      api.get<DuplicateListResponse>(
        `/api/duplicates?tab=needs_review&limit=500${q ? `&q=${encodeURIComponent(q)}` : ''}`),
  })
  const applyAll = useMutation({
    mutationFn: () => api.post<ApplyAutoMergesResponse>('/api/duplicates/apply_auto_merges', {}),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['duplicates'] })
      qc.invalidateQueries({ queryKey: ['navrail-dashboard-counts'] })
    },
  })

  if (activeCandidate !== null) {
    return (
      <DuplicateDetail
        candidateId={activeCandidate}
        onBack={() => setActiveCandidate(null)}
      />
    )
  }

  const searchBox = (
    <div className="flex items-center gap-3">
      <input
        type="search"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        placeholder="Find a pair by name, email or account number"
        aria-label="Find a duplicate pair"
        className="w-full max-w-md bg-ink-900 border border-ink-500 rounded-sm px-3 py-2 text-[13px] text-paper placeholder:text-paper-trace focus:outline-none focus:border-amber-glow"
      />
      {search && (
        <button onClick={() => setSearch('')} className="font-mono text-[11px] text-paper-dim hover:text-paper">
          clear
        </button>
      )}
    </div>
  )

  if (isLoading || !data) {
    return (
      <div className="space-y-6">
        {searchBox}
        <div className="label animate-blink">loading…</div>
      </div>
    )
  }

  const items = data.items as DuplicateCandidate[]

  if (q) {
    return (
      <div className="space-y-4">
        {searchBox}
        <div className="label">
          {data.total} pending pair{data.total === 1 ? '' : 's'} matching “{q}”
        </div>
        {items.length === 0 ? (
          <EmptySectionNote>No pending pair has a member matching that.</EmptySectionNote>
        ) : (
          items.map((c) => <DuplicatePairCard key={c.id} candidate={c} onReview={setActiveCandidate} />)
        )}
      </div>
    )
  }

  // Group by tier but preserve the (score desc) order the server gave us.
  const byTier: Record<DuplicateTier, DuplicateCandidate[]> = {
    auto_merge: [],
    review_high: [],
    review_med: [],
    ignore: [],
  }
  for (const c of items) byTier[c.tier].push(c)

  const tc = data.tier_counts

  return (
    <div className="space-y-6">
      {searchBox}

      {/* SECTION 1 — auto-merge queue */}
      <section>
        <div className="flex items-center justify-between mb-2">
          <div>
            <h2 className="font-serif text-[15px] text-amber-glow">Ready to auto-merge</h2>
            <p className="text-paper-dim text-[12px] max-w-xl">
              Two or more exact field matches; score ≥ 0.90. Safe to collapse in one click — each
              merge is reversible for 7 days.
            </p>
          </div>
          <div className="flex items-center gap-3">
            <div className="font-mono text-[11px] text-paper-dim tabular-nums">
              {tc.auto_merge} pair{tc.auto_merge === 1 ? '' : 's'}
            </div>
            {tc.auto_merge > 0 && (
              <Button
                variant="primary"
                onClick={() => applyAll.mutate()}
                disabled={applyAll.isPending}
              >
                {applyAll.isPending ? 'applying…' : `Apply all ${tc.auto_merge}`}
              </Button>
            )}
          </div>
        </div>

        {applyAll.data && (
          <div className="mb-3 px-3 py-2 border border-amber-glow/30 bg-amber-glow/5 rounded-sm text-[12px] font-mono text-paper-dim">
            applied {applyAll.data.applied} ·
            skipped (disqualified): {applyAll.data.skipped_disqualified} ·
            skipped (tier): {applyAll.data.skipped_not_auto_merge_tier} ·
            failed: {applyAll.data.failed}
          </div>
        )}

        {byTier.auto_merge.length === 0 ? (
          <EmptySectionNote>
            Nothing in the auto-merge queue. The nightly detection job adds pairs here when two
            or more fields match exactly (email + name, email + zip, phone + name, etc.).
          </EmptySectionNote>
        ) : (
          <PairList items={byTier.auto_merge} total={tc.auto_merge} first={20} onReview={setActiveCandidate} />
        )}
      </section>

      {/* SECTION 2 — high-confidence review */}
      <CollapsibleSection
        title="High-confidence review"
        subtitle="Score ≥ 0.75. Strong but not two-factor; worth a human eye before merging."
        count={tc.review_high}
      >
        <PairList items={byTier.review_high} total={tc.review_high} first={50} onReview={setActiveCandidate} />
      </CollapsibleSection>

      {/* SECTION 3 — medium-confidence review */}
      <CollapsibleSection
        title="Medium-confidence review"
        subtitle="Score 0.60 – 0.75. Often ambiguous (one field match plus a fuzzy second)."
        count={tc.review_med}
        defaultCollapsed
      >
        <PairList items={byTier.review_med} total={tc.review_med} first={50} onReview={setActiveCandidate} />
      </CollapsibleSection>
    </div>
  )
}

/** The first `first` pairs of a tier, with a button to show every loaded one.
 * Only the top 500 pairs (by score) are loaded; past that, use the search box. */
function PairList({ items, total, first, onReview }: {
  items: DuplicateCandidate[]; total: number; first: number; onReview: (id: number) => void
}) {
  const [all, setAll] = useState(false)
  const shown = all ? items : items.slice(0, first)
  return (
    <div>
      {shown.map((c) => <DuplicatePairCard key={c.id} candidate={c} onReview={onReview} />)}
      <div className="flex items-center justify-center gap-3 py-2 text-paper-trace text-[11px] font-mono">
        {items.length > first && (
          <button onClick={() => setAll(!all)} className="text-paper-dim hover:text-paper underline">
            {all ? `show first ${first}` : `show all ${items.length}`}
          </button>
        )}
        {(all || items.length <= first) && total > items.length && (
          <span>{total - items.length} more not loaded — use the search box to find them</span>
        )}
      </div>
    </div>
  )
}

function EmptySectionNote({ children }: { children: React.ReactNode }) {
  return (
    <div className="p-6 text-center text-paper-dim text-[12px] border border-dashed border-ink-500 rounded-sm">
      {children}
    </div>
  )
}

function CollapsibleSection({
  title, subtitle, count, children, defaultCollapsed = false,
}: {
  title: string
  subtitle: string
  count: number
  children: React.ReactNode
  defaultCollapsed?: boolean
}) {
  const [open, setOpen] = useState(!defaultCollapsed)
  return (
    <section>
      <div className="flex items-center justify-between mb-2">
        <div>
          <button
            onClick={() => setOpen(!open)}
            className="font-serif text-[15px] text-paper hover:text-amber-glow"
          >
            {open ? '▾' : '▸'} {title}
          </button>
          <p className="text-paper-dim text-[12px] max-w-xl">{subtitle}</p>
        </div>
        <div className="font-mono text-[11px] text-paper-dim tabular-nums">
          {count} pair{count === 1 ? '' : 's'}
        </div>
      </div>
      {open && (count === 0 ? (
        <EmptySectionNote>Nothing here right now.</EmptySectionNote>
      ) : children)}
    </section>
  )
}

function SubscriberMini({ s }: { s: DuplicateSubscriberSummary | null }) {
  if (!s) return <span className="text-paper-trace italic">(missing)</span>
  const name = [s.first_name, s.last_name].filter(Boolean).join(' ') || '—'
  return (
    <div className="space-y-0.5">
      <div className="text-paper">{name}</div>
      <div className="text-paper-trace text-[11px] font-mono">{s.email ?? 'no email'}</div>
      <div className="text-paper-trace text-[11px] font-mono">
        acct {s.simplecirc_account_id}
        {s.city && s.state ? ` · ${s.city}, ${s.state}` : ''}
      </div>
    </div>
  )
}

// ---------- Detail view (side-by-side) ----------

function DuplicateDetail({
  candidateId, onBack,
}: { candidateId: number; onBack: () => void }) {
  const qc = useQueryClient()
  const { data, isLoading } = useQuery({
    queryKey: ['duplicate', candidateId],
    queryFn: () => api.get<DuplicateDetailResponse>(`/api/duplicates/${candidateId}`),
  })

  const [chosenWinner, setChosenWinner] = useState<number | null>(null)

  const merge = useMutation({
    mutationFn: (winnerId: number | null) =>
      api.post(`/api/duplicates/${candidateId}/merge`, {
        winner_id: winnerId,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['duplicates'] })
      onBack()
    },
  })
  const dismiss = useMutation({
    mutationFn: () => api.post(`/api/duplicates/${candidateId}/dismiss`, {}),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['duplicates'] }); onBack() },
  })
  const keepRelated = useMutation({
    mutationFn: () => api.post(`/api/duplicates/${candidateId}/keep_related`, {}),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['duplicates'] }); onBack() },
  })

  if (isLoading || !data) return <div className="label animate-blink">loading…</div>

  const { candidate, a, b, recommended_winner_id } = data
  const selected = chosenWinner ?? recommended_winner_id

  return (
    <div className="space-y-5">
      <div className="flex items-center gap-3">
        <button onClick={onBack} className="label hover:text-paper">← back to list</button>
        <span className="text-paper-trace text-[11px]">
          score {candidate.score.toFixed(2)} · source {candidate.source}
        </span>
      </div>

      <Panel>
        <div className="grid grid-cols-2 divide-x divide-ink-500">
          <SubscriberSide
            sub={a}
            side="A"
            other={{ side: 'B', sub: b }}
            giftLinks={candidate.gift_links}
            selected={selected === a.id}
            recommended={recommended_winner_id === a.id}
            onChoose={() => setChosenWinner(a.id)}
          />
          <SubscriberSide
            sub={b}
            side="B"
            other={{ side: 'A', sub: a }}
            giftLinks={candidate.gift_links}
            selected={selected === b.id}
            recommended={recommended_winner_id === b.id}
            onChoose={() => setChosenWinner(b.id)}
          />
        </div>
      </Panel>

      <Panel>
        <div className="p-4 space-y-3">
          <div className="label">Matching evidence</div>
          <div className="text-[13px] text-paper-dim space-y-1">
            {(candidate.reason_codes ?? []).map((rc, i) => (
              <div key={i} className="font-mono">
                <span className="text-paper">{rc.feature}</span>
                <span className="text-paper-trace"> ({rc.kind}, +{rc.weight})</span>
                {rc.detail ? <span className="text-paper-trace"> — {rc.detail}</span> : null}
              </div>
            ))}
          </div>
        </div>
      </Panel>

      {candidate.gift_links?.length > 0 && (
        <div className="px-3 py-2 border border-radar-green/40 bg-radar-green/5 rounded-sm text-[12px] text-paper-dim max-w-2xl">
          One of these accounts bought a gift subscription for the other. That usually means two
          people sharing an email or address, so <span className="text-paper">Keep both, related</span> is
          the usual choice.
        </div>
      )}

      <div className="flex gap-3">
        <Button
          onClick={() => merge.mutate(selected)}
          disabled={selected === null || merge.isPending}
          variant="primary"
        >
          {merge.isPending ? 'merging…' : `Merge — winner ${selected === a.id ? 'A' : 'B'}`}
        </Button>
        <Button onClick={() => dismiss.mutate()} disabled={dismiss.isPending}>
          Not a duplicate
        </Button>
        <Button onClick={() => keepRelated.mutate()} disabled={keepRelated.isPending}>
          Keep both, related
        </Button>
        <Button onClick={onBack}>Defer</Button>
      </div>
      {merge.isError && (
        <div className="text-red-400 text-[12px] font-mono">
          {(merge.error as Error).message}
        </div>
      )}
    </div>
  )
}

function SubscriberSide({
  sub, side, other, giftLinks, selected, recommended, onChoose,
}: {
  sub: DuplicateSubscriberDetail
  side: string
  other: { side: string; sub: DuplicateSubscriberDetail }
  giftLinks: DuplicateGiftLink[]
  selected: boolean
  recommended: boolean
  onChoose: () => void
}) {
  return (
    <div className={`p-4 ${selected ? 'bg-amber-glow/5' : ''}`}>
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <div className="font-serif text-xl text-paper">{side}</div>
          <GiftRoleTag role={giftRole(giftLinks, sub.id)} />
        </div>
        <button
          onClick={onChoose}
          className={`text-[11px] font-mono px-2 py-1 border rounded ${
            selected
              ? 'border-amber-glow text-amber-glow'
              : 'border-ink-500 text-paper-dim hover:text-paper'
          }`}
        >
          {selected ? '● selected winner' : '○ pick as winner'}
          {recommended ? ' (rec)' : ''}
        </button>
      </div>
      <div className="space-y-1 text-[13px]">
        <Row label="account_id" value={sub.simplecirc_account_id} mono />
        <Row label="name" value={[sub.first_name, sub.last_name].filter(Boolean).join(' ') || '—'} />
        <Row label="email" value={sub.email ?? '—'} mono />
        <Row label="phone" value={sub.phone ?? '—'} mono />
        <Row label="address" value={
          [sub.address_1, sub.address_2, [sub.city, sub.state, sub.zipcode].filter(Boolean).join(' ')]
            .filter(Boolean).join(', ') || '—'
        } />
        <Row
          label="since"
          value={sub.member_since?.slice(0, 10) ?? '—'}
          mono
          hint="Earliest order_events.order_date (fallback: first subscription start). Drives the survivorship rule's account-age tiebreaker."
        />
        <Row label="last sync" value={sub.last_synced_at?.slice(0, 10) ?? '—'} mono />
      </div>
      <GiftLines sub={sub} other={other} links={giftLinks} />
      <div className="mt-3">
        <div className="label mb-1">Subscriptions</div>
        {sub.subscriptions.length === 0 ? (
          <div className="text-paper-trace text-[12px]">none</div>
        ) : (
          <ul className="space-y-0.5 text-[12px] font-mono">
            {sub.subscriptions.map((s) => (
              <li key={s.simplecirc_subscription_id} className="text-paper-dim">
                {publicationName(s.publication_slug)} — {s.status}
                {s.auto_renew ? ' · auto-renew' : ''}
                {s.current_period_end ? ` · ends ${s.current_period_end}` : ''}
              </li>
            ))}
          </ul>
        )}
      </div>
      <div className="mt-3">
        <Link
          to={`/subscribers/${sub.id}`}
          className="text-[11px] font-mono text-paper-trace hover:text-paper underline"
        >
          open subscriber →
        </Link>
      </div>
    </div>
  )
}

/** "Gave a gift subscription to B (acct …) on RC Car Action · last order 2025-09-16". */
function GiftLines({ sub, other, links }: {
  sub: DuplicateSubscriberDetail
  other: { side: string; sub: DuplicateSubscriberDetail }
  links: DuplicateGiftLink[]
}) {
  const mine = (links ?? []).filter((l) => l.giver_id === sub.id || l.receiver_id === sub.id)
  if (mine.length === 0) return null
  return (
    <div className="mt-3">
      <div className="label mb-1">Gift</div>
      <ul className="space-y-0.5 text-[12px]">
        {mine.map((l) => {
          const gave = l.giver_id === sub.id
          return (
            <li key={`${l.giver_id}-${l.receiver_id}-${l.publication_slug}`} className="text-paper-dim">
              <span className={gave ? 'text-radar-green' : 'text-radar-cyan'}>
                {gave ? 'Gave a gift subscription to' : 'Got its subscription as a gift from'}
              </span>{' '}
              {other.side} <span className="font-mono">(acct {other.sub.simplecirc_account_id})</span>
              {' '}on {publicationName(l.publication_slug)}
              <span className="font-mono text-paper-trace">
                {l.last_order_date ? ` · last order ${l.last_order_date}` : ''}
                {l.orders > 1 ? ` · ${l.orders} orders` : ''}
              </span>
            </li>
          )
        })}
      </ul>
    </div>
  )
}

function Row({ label, value, mono, hint }: {
  label: string
  value: string
  mono?: boolean
  hint?: string
}) {
  return (
    <div className="flex gap-3">
      <div className="label w-20 shrink-0" title={hint}>{label}</div>
      <div className={`${mono ? 'font-mono' : ''} text-paper`}>{value}</div>
    </div>
  )
}

// ---------- Auto-merged ----------

function AutoMergedTab() {
  const qc = useQueryClient()
  const { data, isLoading } = useQuery({
    queryKey: ['duplicates', 'auto_merged'],
    queryFn: () => api.get<DuplicateListResponse>('/api/duplicates?tab=auto_merged'),
  })
  const undo = useMutation({
    mutationFn: (auditId: number) =>
      api.post(`/api/duplicates/merge_audit/${auditId}/undo`, {}),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['duplicates'] }),
  })

  if (isLoading || !data) return <div className="label animate-blink">loading…</div>
  const items = data.items as DuplicateAutoMergedItem[]

  return (
    <Panel>
      <div className="px-4 py-3 border-b border-ink-500">
        <div className="label">{data.total} auto-merge{data.total === 1 ? '' : 's'} within the 7-day undo window</div>
      </div>
      {items.length === 0 ? (
        <div className="p-8 text-center text-paper-dim text-[13px]">
          No recent auto-merges within the undo window.
        </div>
      ) : (
        <table className="w-full text-[13px]">
          <thead className="text-paper-trace label">
            <tr className="border-b border-ink-500">
              <th className="text-left px-4 py-2">When</th>
              <th className="text-left px-4 py-2">Winner (canonical)</th>
              <th className="text-left px-4 py-2">Loser (merged-away)</th>
              <th className="text-left px-4 py-2">Undo expires</th>
              <th className="text-right px-4 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {items.map((m) => (
              <tr key={m.audit_id} className="border-b border-ink-500/40">
                <td className="px-4 py-3 font-mono">{m.performed_at.slice(0, 16).replace('T', ' ')}</td>
                <td className="px-4 py-3"><SubscriberMini s={m.winner_summary} /></td>
                <td className="px-4 py-3 text-paper-dim">
                  <div>{[m.loser_summary.first_name, m.loser_summary.last_name].filter(Boolean).join(' ') || '—'}</div>
                  <div className="text-paper-trace text-[11px] font-mono">{m.loser_summary.email ?? 'no email'}</div>
                  <div className="text-paper-trace text-[11px] font-mono">acct {m.loser_summary.simplecirc_account_id ?? '—'}</div>
                </td>
                <td className="px-4 py-3 font-mono text-paper-dim">{m.undo_deadline.slice(0, 16).replace('T', ' ')}</td>
                <td className="px-4 py-3 text-right">
                  <Button
                    onClick={() => undo.mutate(m.audit_id)}
                    disabled={undo.isPending}
                  >
                    Undo
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </Panel>
  )
}

// ---------- Upstream cleanup ----------

function UpstreamCleanupTab() {
  const qc = useQueryClient()
  const { data, isLoading } = useQuery({
    queryKey: ['duplicates', 'upstream_cleanup'],
    queryFn: () => api.get<{ total: number; items: UpstreamCleanupItem[] }>(
      '/api/duplicates/upstream_cleanup'
    ),
  })
  const markDone = useMutation({
    mutationFn: (cleanupId: number) =>
      api.post(`/api/duplicates/upstream_cleanup/${cleanupId}/done`, {}),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['duplicates', 'upstream_cleanup'] }),
  })

  if (isLoading || !data) return <div className="label animate-blink">loading…</div>

  return (
    <Panel>
      <div className="px-4 py-3 border-b border-ink-500 space-y-1">
        <div className="label">{data.total} SimpleCirc account{data.total === 1 ? '' : 's'} pending manual consolidation</div>
        <div className="text-paper-dim text-[12px]">
          SimpleCirc has no merge API. These are the loser accounts still alive upstream;
          an operator should consolidate them in SimpleCirc's dashboard, then check them off here.
        </div>
      </div>
      {data.items.length === 0 ? (
        <div className="p-8 text-center text-paper-dim text-[13px]">
          Nothing pending upstream cleanup.
        </div>
      ) : (
        <table className="w-full text-[13px]">
          <thead className="text-paper-trace label">
            <tr className="border-b border-ink-500">
              <th className="text-left px-4 py-2">Queued</th>
              <th className="text-left px-4 py-2">Loser account (to close in SimpleCirc)</th>
              <th className="text-left px-4 py-2">Canonical (keeps the subscription)</th>
              <th className="text-right px-4 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {data.items.map((r) => (
              <tr key={r.id} className="border-b border-ink-500/40">
                <td className="px-4 py-3 font-mono text-paper-dim">{r.added_at.slice(0, 10)}</td>
                <td className="px-4 py-3 font-mono">{r.loser_simplecirc_account_id}</td>
                <td className="px-4 py-3">
                  <div>{[r.canonical.first_name, r.canonical.last_name].filter(Boolean).join(' ') || '—'}</div>
                  <div className="text-paper-trace text-[11px] font-mono">{r.canonical.email ?? 'no email'}</div>
                  <div className="text-paper-trace text-[11px] font-mono">acct {r.canonical.simplecirc_account_id ?? '—'}</div>
                </td>
                <td className="px-4 py-3 text-right">
                  <Button
                    onClick={() => markDone.mutate(r.id)}
                    disabled={markDone.isPending}
                  >
                    Mark done
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </Panel>
  )
}

// ---------- Dismissed ----------

function DismissedTab() {
  const { data, isLoading } = useQuery({
    queryKey: ['duplicates', 'dismissed'],
    queryFn: () => api.get<DuplicateListResponse>('/api/duplicates?tab=dismissed'),
  })
  if (isLoading || !data) return <div className="label animate-blink">loading…</div>
  const items = data.items as DuplicateCandidate[]
  return (
    <Panel>
      <div className="px-4 py-3 border-b border-ink-500">
        <div className="label">{data.total} previously dismissed or kept-as-related</div>
      </div>
      {items.length === 0 ? (
        <div className="p-8 text-center text-paper-dim text-[13px]">Nothing dismissed yet.</div>
      ) : (
        <table className="w-full text-[13px]">
          <thead className="text-paper-trace label">
            <tr className="border-b border-ink-500">
              <th className="text-left px-4 py-2">Status</th>
              <th className="text-left px-4 py-2">A</th>
              <th className="text-left px-4 py-2">B</th>
              <th className="text-left px-4 py-2">Resolved</th>
            </tr>
          </thead>
          <tbody>
            {items.map((c) => (
              <tr key={c.id} className="border-b border-ink-500/40">
                <td className="px-4 py-3 text-paper-dim">{c.status}</td>
                <td className="px-4 py-3"><SubscriberMini s={c.a} /></td>
                <td className="px-4 py-3"><SubscriberMini s={c.b} /></td>
                <td className="px-4 py-3 font-mono text-paper-trace">
                  {c.resolved_at?.slice(0, 10) ?? '—'}
                  {c.resolved_by ? ` by ${c.resolved_by}` : ''}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </Panel>
  )
}
